import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  fetchLearnerEnrollments,
  fetchLearnerPortal,
  type PortalLearner,
  type PortalEnrollment,
  type PortalRegistration,
} from '../../api/learnPublicApi';
import {
  WarningCircle, CalendarBlank, Clock, MapPin, MagnifyingGlass,
  Monitor, ArrowRight, BookOpen, XCircle, CheckCircle,
  HourglassSimple, CaretRight, ListChecks,
} from '@phosphor-icons/react';
import { useTraining } from './TrainingContext';

/* ── Helpers ── */

function formatDate(dateStr?: string | null) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

function formatTime(timeStr?: string | null) {
  if (!timeStr) return '';
  if (timeStr.startsWith('0000') || timeStr === '00:00') return '';
  if (timeStr.includes('T')) {
    const d = new Date(timeStr);
    if (isNaN(d.getTime()) || (d.getHours() === 0 && d.getMinutes() === 0 && d.getFullYear() < 1900)) return '';
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }
  const [h, m] = timeStr.split(':').map(Number);
  if (h === 0 && m === 0) return '';
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

function parseDateBlock(dateStr?: string | null) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return {
    month: d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
    day: d.getDate(),
  };
}

function isVirtual(mode?: string): boolean {
  if (!mode) return false;
  const m = mode.toLowerCase();
  return m === 'virtual' || m === 'vilt';
}

const STATUS_CONFIG: Record<string, { cls: string; icon: typeof CheckCircle }> = {
  registered:  { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
  approved:    { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
  confirmed:   { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
  attended:    { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
  accepted:    { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
  pending:     { cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: HourglassSimple },
  waitlisted:  { cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: HourglassSimple },
  invited:     { cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: HourglassSimple },
  cancelled:   { cls: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
};

function getStatusConfig(status: string) {
  return STATUS_CONFIG[status] || { cls: 'bg-gray-50 text-gray-600 border-gray-200', icon: CheckCircle };
}

/* ── Unified display type ── */
interface DisplayItem {
  id: string;
  type: 'enrollment' | 'registration';
  event_name: string;
  session_name: string | null;
  status: string;
  start_date: string | null;
  start_time: string | null;
  location_name: string;
  event_mode: string;
  course_name: string;
  course_thumbnail: string;
  cancel_token?: string;
  linkTo: string;
}

/* ── Main Component ── */

export default function TrainingPortalPage() {
  const navigate = useNavigate();
  const { magicToken, catalogPath, setPageTitle } = useTraining();

  const [learner, setLearner] = useState<PortalLearner | null>(null);
  const [items, setItems] = useState<DisplayItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { setPageTitle('My Registrations'); }, [setPageTitle]);

  useEffect(() => {
    if (!magicToken) {
      setError('No portal token provided. Check your email for the correct link.');
      setLoading(false);
      return;
    }

    fetchLearnerEnrollments(magicToken)
      .then((data) => {
        setLearner(data.learner);
        // org branding handled by TrainingContext
        const enrollments: PortalEnrollment[] = data.enrollments || [];
        const displayItems: DisplayItem[] = enrollments.map((e) => ({
          id: String(e.enrollment_id),
          type: 'enrollment' as const,
          event_name: e.event_name,
          session_name: e.session_name,
          status: e.enrollment_status,
          start_date: e.session_start_date,
          start_time: e.session_start_time,
          location_name: e.location_name,
          event_mode: e.event_mode,
          course_name: e.course_name,
          course_thumbnail: e.course_thumbnail,
          linkTo: `/training/registration/${e.enrollment_id}?t=${e.access_token || magicToken}`,
        }));
        setItems(displayItems);
      })
      .catch(() => {
        return fetchLearnerPortal(magicToken).then((data) => {
          setLearner(data.learner);
          const regs: PortalRegistration[] = data.registrations || [];
          const displayItems: DisplayItem[] = regs.map((r) => ({
            id: String(r.participant_id),
            type: 'registration' as const,
            event_name: r.event_name,
            session_name: r.session_name,
            status: r.status || 'unknown',
            start_date: r.start_date,
            start_time: r.start_time,
            location_name: r.location_name,
            event_mode: r.event_mode,
            course_name: r.course_name,
            course_thumbnail: '',
            cancel_token: r.cancel_token,
            linkTo: `/training/registration/${r.participant_id}?t=${magicToken}&legacy=1`,
          }));
          setItems(displayItems);
        });
      })
      .catch(() => setError('Invalid or expired portal link.'))
      .finally(() => setLoading(false));
  }, [magicToken]);

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
      </div>
    );
  }

  /* ── Error ── */
  if (error || !learner) {
    return (
      <div className="flex items-center justify-center px-4 py-16">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-10 max-w-md w-full text-center">
          <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <WarningCircle size={28} weight="duotone" className="text-red-500" />
          </div>
          <h2 className="text-lg font-bold text-gray-900 mb-2">Portal Access Error</h2>
          <p className="text-sm text-gray-500 leading-relaxed mb-6">{error}</p>
          <Link
            to={catalogPath}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-700"
          >
            Browse Catalog <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </div>
    );
  }

  const active = items.filter((i) => i.status !== 'cancelled');
  const cancelled = items.filter((i) => i.status === 'cancelled');
  const upcoming = active.filter((i) => {
    if (!i.start_date) return false;
    return new Date(i.start_date) >= new Date(new Date().toDateString());
  });

  return (
    <div>
      <main className="max-w-[880px] mx-auto px-6 pt-8 pb-20">
        {/* ── Summary Stats ── */}
        <div className="grid grid-cols-3 gap-3.5 mb-8">
          <StatCard label="Active" value={active.length} color="text-emerald-600" />
          <StatCard label="Upcoming" value={upcoming.length} color="text-indigo-600" />
          <StatCard label="Total" value={items.length} color="text-gray-700" />
        </div>

        {/* ── Empty state ── */}
        {active.length === 0 && cancelled.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-5">
              <CalendarBlank size={32} weight="duotone" className="text-indigo-500" />
            </div>
            <h3 className="text-base font-semibold text-gray-800 mb-1.5">No registrations yet</h3>
            <p className="text-sm text-gray-500 mb-5 leading-relaxed">
              Browse available training events and register for sessions.
            </p>
            <Link
              to={catalogPath}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors"
            >
              <MagnifyingGlass size={16} weight="bold" />
              Browse Catalog
            </Link>
          </div>
        )}

        {/* ── Active Registrations ── */}
        {active.length > 0 && (
          <section className="mb-10">
            <SectionLabel label="Active Registrations" count={active.length} />
            <div className="flex flex-col gap-3">
              {active.map((item) => (
                <RegistrationCard key={item.id} item={item} navigate={navigate} />
              ))}
            </div>
          </section>
        )}

        {/* ── Cancelled ── */}
        {cancelled.length > 0 && (
          <section className="mb-10">
            <SectionLabel label="Cancelled" count={cancelled.length} muted />
            <div className="flex flex-col gap-2.5">
              {cancelled.map((item) => (
                <RegistrationCard key={item.id} item={item} navigate={navigate} isCancelled />
              ))}
            </div>
          </section>
        )}

        {/* ── Browse link ── */}
        {items.length > 0 && (
          <div className="text-center pt-2">
            <Link
              to={catalogPath}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700"
            >
              <MagnifyingGlass size={15} weight="bold" />
              Browse More Events
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}

/* ── Section Label (matches internal pattern) ── */

function SectionLabel({ label, count, muted }: { label: string; count: number; muted?: boolean }) {
  return (
    <div className="flex items-center gap-3 mb-3.5">
      <div className="flex-1 h-px bg-gray-200" />
      <span className={`text-xs font-semibold uppercase tracking-widest whitespace-nowrap ${muted ? 'text-gray-300' : 'text-gray-400'}`}>
        {label} ({count})
      </span>
      <div className="flex-1 h-px bg-gray-200" />
    </div>
  );
}

/* ── Stat Card ── */

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 text-center">
      <div className={`text-3xl font-bold leading-none ${color}`}>{value}</div>
      <div className="text-[11px] font-semibold text-gray-400 mt-1 uppercase tracking-wider">{label}</div>
    </div>
  );
}

/* ── Registration Card ── */

function RegistrationCard({
  item, navigate, isCancelled,
}: {
  item: DisplayItem;
  navigate: ReturnType<typeof useNavigate>;
  isCancelled?: boolean;
}) {
  const statusCfg = getStatusConfig(item.status);
  const StatusIcon = statusCfg.icon;
  const dateBlock = parseDateBlock(item.start_date);
  const timeStr = formatTime(item.start_time);
  const dateStr = formatDate(item.start_date);
  const virtual = isVirtual(item.event_mode);

  return (
    <div
      onClick={() => !isCancelled && navigate(item.linkTo)}
      className={`bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm transition-all
        ${isCancelled ? 'opacity-60 cursor-default' : 'cursor-pointer hover:shadow-md hover:border-indigo-200'}`}
    >
      <div className="flex items-stretch">
        {/* Date column */}
        <div className={`w-[68px] min-w-[68px] flex flex-col items-center justify-center py-4
          ${isCancelled ? 'bg-gray-100' : 'bg-indigo-50'}`}
        >
          {dateBlock ? (
            <>
              <div className={`text-[11px] font-bold uppercase tracking-wide leading-none
                ${isCancelled ? 'text-gray-400' : 'text-indigo-600'}`}>
                {dateBlock.month}
              </div>
              <div className={`text-2xl font-extrabold leading-tight
                ${isCancelled ? 'text-gray-500' : 'text-indigo-800'}`}>
                {dateBlock.day}
              </div>
            </>
          ) : (
            <div className="text-xs text-gray-400 font-semibold">TBD</div>
          )}
        </div>

        {/* Main content */}
        <div className="flex-1 p-3.5 min-w-0 flex flex-col justify-center">
          {/* Top row */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-[15px] font-semibold text-gray-900 leading-snug truncate">
                {item.event_name}
              </div>
              {item.session_name && (
                <div className="text-[13px] text-gray-500 mt-0.5 truncate">{item.session_name}</div>
              )}
            </div>
            <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-full border flex-shrink-0 capitalize ${statusCfg.cls}`}>
              <StatusIcon size={12} weight="fill" />
              {item.status}
            </span>
          </div>

          {/* Info row */}
          <div className="flex items-center flex-wrap gap-x-3.5 gap-y-1 mt-2.5">
            {dateStr && (
              <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                <CalendarBlank size={13} weight="bold" className="text-gray-400" />
                {dateStr}
                {timeStr && <span className="text-gray-300">&middot;</span>}
                {timeStr && (
                  <>
                    <Clock size={13} weight="bold" className="text-gray-400" />
                    {timeStr}
                  </>
                )}
              </span>
            )}
            {(item.location_name || virtual) && (
              <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                {virtual
                  ? <><Monitor size={13} weight="bold" className="text-gray-400" /> Virtual</>
                  : <><MapPin size={13} weight="bold" className="text-gray-400" /> {item.location_name}</>
                }
              </span>
            )}
            {item.course_name && (
              <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                <BookOpen size={13} weight="bold" className="text-gray-400" />
                {item.course_name}
              </span>
            )}
          </div>

          {/* Actions row */}
          <div className="flex items-center gap-3 mt-2.5">
            {!isCancelled && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600">
                <ListChecks size={13} weight="bold" />
                View Steps & Progress <CaretRight size={12} weight="bold" />
              </span>
            )}
            {item.cancel_token && item.status !== 'cancelled' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/training/cancel/${item.cancel_token}`);
                }}
                className="text-xs font-medium text-red-500 hover:text-red-600 bg-transparent border-none cursor-pointer ml-auto p-0"
              >
                Cancel Registration
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
